import { Injectable } from '@angular/core';
import { VoiceRecognitionService } from '@shared/services/voice-recognition.service';
import { SimplifiedRegex } from '@shared/value-objects/simplified-regex';
import { uniqueId } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

export interface VoiceCommandRequest {
  trigger: string;
  action: Function;
  group: string;
}

export interface VoiceCommand extends VoiceCommandRequest {
  id: string;
  regex: RegExp;
}

export const NON_REMOVABLE_GROUP = 'speech-main';

@Injectable({
  providedIn: 'root',
})
export class VoiceCommandService {
  private commands: VoiceCommand[] = [];
  private handleCommandsSubject = new BehaviorSubject<boolean>(true);

  constructor(private voiceRecognitionService: VoiceRecognitionService) {
    this.voiceRecognitionService
      .subscribeTranscript()
      .subscribe(transcript => this.runTranscript(transcript));
  }

  private runTranscript(transcript: string[]): void {
    const transcriptMatch = transcript.find(result =>
      this.commandMatch(result)
    );

    if (!transcriptMatch) return;

    const commandObject = this.commandMatch(transcriptMatch);
    if (!this.isActive() && commandObject.group !== NON_REMOVABLE_GROUP) {
      return;
    }

    this.executeCommand(transcript, commandObject);
  }

  private executeCommand(transcript: string[], command: VoiceCommand): void {
    const values = transcript
      .map(text => {
        const regexArg = command.regex.exec(text);
        if (!regexArg) return;
        return regexArg.map(arg => arg);
      })
      .filter(text => text);

    const transpose = <T>(argumentArray: T[][]): T[][] =>
      argumentArray[0].map((_, c) => argumentArray.map(r => r[c]));

    const args = transpose(values);

    command.action(...args);
  }

  public activate(): void {
    this.handleCommandsSubject.next(true);
  }

  public halt(): void {
    this.handleCommandsSubject.next(false);
  }

  public isActive(): boolean {
    return this.handleCommandsSubject.value;
  }

  public isActiveObservable(): Observable<boolean> {
    return this.handleCommandsSubject.asObservable();
  }

  public getCommands(): VoiceCommand[] {
    return this.commands.map(command => ({ ...command }));
  }

  public addCommands(commands: VoiceCommandRequest[]): VoiceCommand[] {
    const transformedCommands = commands.map(command =>
      this.transformCommandRequest(command)
    );

    this.commands = [
      ...this.commands,
      ...transformedCommands.filter(command => !this.commandExists(command)),
    ];

    return this.getCommands();
  }

  private commandExists(newCommand: VoiceCommand): boolean {
    return this.commands.some(
      command => command.trigger === newCommand.trigger
    );
  }

  public removeCommands(commands: VoiceCommand[]): VoiceCommand[] {
    if (!commands?.length) {
      this.commands = this.commands.filter(
        command => command.group === NON_REMOVABLE_GROUP
      );
    } else {
      const containId = <T extends { id: any }>(array: T[], match: T) =>
        array.some(item => item.id === match.id);

      this.commands = this.commands.filter(
        command =>
          !containId<VoiceCommand>(commands, command) ||
          command.group === NON_REMOVABLE_GROUP
      );
    }
    return this.getCommands();
  }

  private commandMatch(text: string): VoiceCommand {
    return this.commands.find(command => command.regex?.test(text));
  }

  private transformCommandRequest(command: VoiceCommandRequest): VoiceCommand {
    const regex = new SimplifiedRegex(command.trigger).toRegex();
    return {
      id: uniqueId('command_'),
      ...command,
      regex,
    };
  }
}
